import { Link } from "react-router-dom";
import { CurrencyDollar, MagnifyingGlass, ShieldCheck, PawPrint, Warning } from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import BrowsePreview from "@/components/BrowsePreview";

export default function Landing() {
  return (
    <div className="min-h-screen text-white font-body">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="landing-logo">
            <Logo variant="nav" alt="Hunt57" />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/how-it-works" data-testid="link-how-it-works" className="font-display uppercase text-xs tracking-widest font-bold text-zinc-400 hover:text-white transition-colors">How It Works</Link>
            <Link to="/register" data-testid="link-signup-nav" className="font-display uppercase text-xs tracking-widest font-bold text-zinc-400 hover:text-white transition-colors">Sign Up</Link>
            <Link to="/login" data-testid="link-signin-nav" className="h57-btn-secondary text-xs py-2 px-4">Sign In</Link>
          </nav>
          <Link to="/login" data-testid="link-signin-nav-mobile" className="md:hidden h57-btn-secondary text-xs py-2 px-3">Sign In</Link>
        </div>
      </header>

      {/* Hero — 2-col GRID on md+: left column flows independently of the
          preview's height so tightening the left content never fights the
          taller preview frame on the right. Preview position/size are frozen
          by design — this restructure keeps them at the exact y and 380×283
          box the user approved. */}
      <section className="relative overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-4 md:pt-6 pb-8 md:pb-10">
          <div className="font-mono text-[10px] md:text-[11px] tracking-[0.25em] uppercase text-[#FF5F1F] mb-3" data-testid="hero-tag">
            → Private, invite-only vendors · Zero selling fees · Buyers browse free
          </div>
          <div className="md:grid md:grid-cols-[1fr_380px] md:gap-8" data-testid="hero-logo">
            {/* Left column — logo + everything below it flow naturally */}
            <div>
              <div className="shrink-0 mb-2 md:mb-3">
                <Logo variant="hero" alt="Hunt57 — Hunt. Fetch. Collect." />
              </div>
              <h1 className="font-display font-black tracking-tighter leading-[0.85] text-3xl sm:text-4xl md:text-5xl lg:text-6xl uppercase" data-testid="hero-title">
                Hunt. Fetch. <span className="text-[#FF5F1F]">Collect.</span>
              </h1>
              <p className="mt-3 max-w-2xl text-sm md:text-base text-zinc-300 leading-relaxed font-body" data-testid="hero-desc">
                The private card marketplace — <span className="text-white font-semibold">invite-only vendors, zero seller fees</span>.
                Sign up free to browse slabs, singles, autos, rookies, numbered cards, SSPs, and grails.
                Selling requires a vendor account — apply for review or fast-track with a referral voucher.
              </p>
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <Link to="/register" data-testid="hero-cta-signup" className="h57-btn-primary text-center">
                  Sign Up Free
                </Link>
                <Link to="/login" data-testid="hero-cta-signin" className="h57-btn-secondary text-center">
                  Sign In
                </Link>
              </div>
              <div className="mt-2 font-mono text-[10px] text-zinc-500 tracking-widest uppercase" data-testid="invite-required-note">
                → Free account to browse & buy. Vendor account = 30-day free trial, then <span className="line-through text-zinc-600">$69.99</span> <span className="text-[#FFD700]">$49.99</span>/yr first year.
              </div>
              <div className="mt-3 font-mono text-[10px] text-zinc-500 tracking-widest uppercase">
                No fees. No noise. Just the hunt.
              </div>
            </div>
            {/* Right column — the preview. Fixed 380×283 (aspect-3/2 of 380).
                Position pinned to top of grid row 1, independent of left col. */}
            <div className="mt-6 md:mt-0">
              <BrowsePreview />
            </div>
          </div>
        </div>
      </section>

      {/* Value grid */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28 border-t border-white/5">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: MagnifyingGlass, tag: "01", title: "Free to Browse & Buy", body: "Sign up in seconds — no invite required. Browse the whole marketplace, message sellers, and build your watchlist right away." },
            { icon: ShieldCheck, tag: "02", title: "Vendor by Invite", body: "Selling is invite-only. Trusted collectors receive referral codes to upgrade to a vendor account and start listing cards." },
            { icon: CurrencyDollar, tag: "03", title: "0% Selling Fees", body: "Vendors keep 100% of every sale. Buyers pay you directly via PayPal, Cash App, or Venmo. Hunt57 never takes a cut." },
          ].map((v, i) => (
            <div key={i} className="h57-surface p-8 relative group hover:border-white/30 transition-colors" data-testid={`value-card-${i}`}>
              <div className="font-mono text-xs text-[#FF5F1F] tracking-widest">{v.tag}</div>
              <v.icon size={32} weight="bold" className="text-[#FF5F1F] mt-6" />
              <h3 className="font-display font-black uppercase text-2xl mt-4 tracking-tight">{v.title}</h3>
              <p className="mt-4 text-zinc-400 leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Membership */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28 border-t border-white/5">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="font-mono text-xs text-[#FF5F1F] tracking-widest uppercase">Vendor Membership</div>
            <h2 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tight mt-4">
              One Yearly Fee.<br />Zero Selling Fees.
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed max-w-lg">
              Browsing and buying is always free. Vendor accounts are invite-only or by application. Card required on file to apply — <span className="text-white">30 days free after approval</span>, then a launch-promo first year at <span className="line-through text-zinc-600">$69.99</span> <span className="text-[#FFD700] font-semibold">$49.99</span>. Renews at <span className="text-white">$69.99/yr</span> on your anniversary date. Cancel any time in your billing portal. Hunt57 never takes a percentage of card sales.
            </p>
          </div>
          <div className="h57-surface p-8">
            <div className="space-y-6">
              {[
                { period: "Apply for vendor status", price: "Free", note: "Add a card — no charge yet." },
                { period: "First 30 days as vendor", price: "$0", note: "Full seller access. Cancel any time." },
                { period: "First-year launch promo", price: "$49.99", note: "One-time promo. Regular $69.99 → $20 off.", strike: "$69.99" },
                { period: "Year 2 onward", price: "$69.99 / yr", note: "Auto-renews on your anniversary date." },
              ].map((t, i) => (
                <div key={i} className="flex items-baseline justify-between gap-4 pb-6 border-b border-white/5 last:border-0 last:pb-0">
                  <div>
                    <div className="font-mono text-[11px] text-zinc-500 tracking-widest uppercase">{t.period}</div>
                    <div className="mt-1 text-zinc-400 text-sm">{t.note}</div>
                  </div>
                  <div className="flex items-baseline gap-2 whitespace-nowrap">
                    {t.strike && (
                      <div className="font-display font-bold text-base md:text-lg text-zinc-500 line-through">{t.strike}</div>
                    )}
                    <div className="font-display font-black text-2xl md:text-3xl text-[#FF5F1F]">{t.price}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 pt-6 border-t border-white/5 flex items-center gap-3">
              <PawPrint size={20} weight="fill" className="text-[#FFD700]" />
              <div className="text-xs text-zinc-400">
                <span className="font-display font-bold uppercase tracking-widest text-[#FFD700]">Founding Members</span>
                {" "}receive lifetime membership while their account remains active and in good standing.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 border-t border-white/5">
        <div className="border border-[#FFD700]/30 bg-[#FFD700]/5 p-6 md:p-8 flex items-start gap-4">
          <Warning size={28} weight="fill" className="text-[#FFD700] shrink-0" />
          <div>
            <div className="font-display uppercase font-bold tracking-widest text-[#FFD700] text-sm">Payment Disclaimer</div>
            <p className="mt-3 text-zinc-300 leading-relaxed max-w-3xl">
              Hunt57 is a listing and discovery platform. Card-sale payments are completed directly between buyer and
              seller through the seller&apos;s chosen payment path. Hunt57 does not process, hold, escrow, refund, or
              guarantee payment for card transactions.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 mt-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Logo variant="sm" alt="Hunt57" />
          </div>
          <div className="flex flex-wrap gap-6 font-display uppercase text-xs tracking-widest font-bold text-zinc-500">
            <Link to="/terms" data-testid="footer-terms" className="hover:text-white">Terms</Link>
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-white">Privacy</Link>
            <Link to="/guidelines" data-testid="footer-guidelines" className="hover:text-white">Community</Link>
            <Link to="/how-it-works" data-testid="footer-how" className="hover:text-white">How It Works</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
